import { type AiAgentId } from '@fruitful/llm-service-shared';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { BodySmallRegular, Spinner, XStack, YStack } from 'smoothie';

import { MutedTableCell } from '../../common/components/GridCellRenderers';
import { useGetMastraThread } from '../hooks/useGetMastraThread';
import { useLiveThreadRefetchInterval } from '../hooks/useLiveThreadRefetchInterval';
import {
    formatWorkingMemoryFieldValue,
    getThreadWorkingMemory,
} from '../utils/threadMetadataUtils';

const WORKING_MEMORY_FIELD_ORDER = [
    'sessionGoal',
    'currentTask',
    'timeframe',
    'include',
    'exclude',
    'constraints',
] as const;

const WORKING_MEMORY_FIELD_ORDER_SET = new Set<string>(
    WORKING_MEMORY_FIELD_ORDER,
);

const WORKING_MEMORY_FIELD_LABELS: Record<string, string> = {
    sessionGoal: 'Session goal',
    currentTask: 'Current task',
    timeframe: 'Timeframe',
    include: 'Include',
    exclude: 'Exclude',
    constraints: 'Constraints',
};

const MONO_STYLE = {
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 18,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
} as const;

interface WorkingMemoryRow {
    id: string;
    label: string;
    value: string;
}

const toWorkingMemoryRows = (
    workingMemory: Record<string, unknown>,
): WorkingMemoryRow[] => {
    const orderedKeys = [
        ...WORKING_MEMORY_FIELD_ORDER.filter((key) => key in workingMemory),
        ...Object.keys(workingMemory).filter(
            (key) => !WORKING_MEMORY_FIELD_ORDER_SET.has(key),
        ),
    ];

    return orderedKeys.map((key) => ({
        id: key,
        label: WORKING_MEMORY_FIELD_LABELS[key] ?? key,
        value: formatWorkingMemoryFieldValue(workingMemory[key]),
    }));
};

export const AiThreadMemoryPanel = ({
    threadId,
    agentId,
}: {
    threadId: string;
    agentId: AiAgentId;
}) => {
    const refetchInterval = useLiveThreadRefetchInterval();

    const {
        data: thread,
        isLoading,
        error,
    } = useGetMastraThread({
        threadId,
        agentId,
        refetchInterval,
    });

    const workingMemory = getThreadWorkingMemory(thread?.metadata);
    const rows =
        workingMemory != null && typeof workingMemory !== 'string'
            ? toWorkingMemoryRows(workingMemory)
            : [];

    if (isLoading) {
        return (
            <XStack justifyContent="center" padding="$6">
                <Spinner />
            </XStack>
        );
    }

    if (error != null) {
        return (
            <YStack padding="$4">
                <BodySmallRegular color="$red900">
                    Unable to load working memory.
                </BodySmallRegular>
            </YStack>
        );
    }

    if (workingMemory == null) {
        return (
            <YStack padding="$4">
                <BodySmallRegular color="$gray700">
                    No working memory recorded for this thread yet.
                </BodySmallRegular>
            </YStack>
        );
    }

    if (typeof workingMemory === 'string') {
        return (
            <YStack padding="$4">
                <BodySmallRegular color="$gray1200" style={MONO_STYLE}>
                    {workingMemory}
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <YStack width="100%" padding="$4">
            <DataTable
                value={rows}
                dataKey="id"
                size="small"
                showGridlines
                stripedRows
                tableStyle={{ width: '100%' }}
            >
                <Column field="label" header="Field" style={{ width: 180 }} />
                <Column
                    field="value"
                    header="Value"
                    body={(row: WorkingMemoryRow) =>
                        row.value === '' ? (
                            <MutedTableCell />
                        ) : (
                            <BodySmallRegular>{row.value}</BodySmallRegular>
                        )
                    }
                />
            </DataTable>
        </YStack>
    );
};
